
import { useRef, useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { menuData } from '../../mocks/menu-data';

export default function MenuPage() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isNavSticky, setIsNavSticky] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  const isSearching = searchQuery.trim().length > 0;

  const filteredData = isSearching
    ? menuData
        .map((category) => ({
          ...category,
          items: category.items.filter((item) =>
            item.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
          ),
        }))
        .filter((category) => category.items.length > 0)
    : menuData;

  const totalResults = filteredData.reduce((sum, c) => sum + c.items.length, 0);

  useEffect(() => {
    const handleScroll = () => {
      const heroBottom = heroRef.current ? heroRef.current.getBoundingClientRect().bottom : 0;
      setIsNavSticky(heroBottom <= 64);

      if (isSearching) return;
      const scrollY = window.scrollY + 140;
      let current = 0;
      categoryRefs.current.forEach((ref, i) => {
        if (ref && ref.offsetTop <= scrollY) {
          current = i;
        }
      });
      setActiveIndex(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSearching]);

  const scrollToCategory = (index: number) => {
    setSearchQuery('');
    setTimeout(() => {
      const ref = categoryRefs.current[index];
      if (ref) {
        const offset = 100;
        const top = ref.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 50);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="relative h-[400px] flex items-center justify-center">
        <img
          src="https://static.readdy.ai/image/356e0a32a00e968a1405b9eb797d2bd9/a2c4d07af9d7030b6f1f0ebc164ab963.jpeg"
          alt="Menu"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center text-white">
          <p className="text-xs tracking-[0.3em] uppercase mb-3">Our Specialties</p>
          <h1 className="text-5xl md:text-6xl font-light tracking-wide">Menu</h1>
        </div>
      </section>

      {/* Category Quick Nav */}
      <div
        ref={navRef}
        className={`z-30 bg-white border-b border-[#F0E8E8] transition-shadow duration-300 ${
          isNavSticky ? 'sticky top-16 shadow-md' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-0">
            {menuData.map((category, index) => (
              <button
                key={index}
                onClick={() => scrollToCategory(index)}
                className={`whitespace-nowrap flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-all duration-200 cursor-pointer ${
                  activeIndex === index && !isSearching
                    ? 'border-[#C0392B] text-[#C0392B]'
                    : 'border-transparent text-[#666666] hover:text-[#2D2D2D] hover:border-[#E8D5D5]'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${category.icon} text-base`}></i>
                </div>
                {category.category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Menu Content */}
      <section className="py-24 bg-white flex-grow">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] text-[#C0392B] uppercase mb-3">Discover Our Flavors</p>
            <h2 className="text-4xl md:text-5xl font-light text-[#2D2D2D] mb-4">Authentic Chinese Cuisine</h2>
            <div className="w-12 h-0.5 bg-[#C0392B] mx-auto mb-10"></div>

            {/* Search Box */}
            <div className="max-w-xl mx-auto relative">
              <div className="w-5 h-5 flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2 text-[#999]">
                <i className="ri-search-line text-lg"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search dishes by name..."
                className="w-full pl-11 pr-10 py-3 text-sm border border-[#E8D5D5] rounded-full bg-[#FAFAFA] text-[#2D2D2D] placeholder-[#BBBBBB] focus:outline-none focus:border-[#C0392B] focus:bg-white transition-all duration-200"
              />
              {isSearching && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#999] hover:text-[#C0392B] transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>

            {/* Search result count */}
            {isSearching && (
              <p className="mt-3 text-sm text-[#999]">
                {totalResults > 0
                  ? `Found ${totalResults} dish${totalResults > 1 ? 'es' : ''} matching "${searchQuery.trim()}"`
                  : `No dishes found for "${searchQuery.trim()}"`}
              </p>
            )}
          </div>

          {/* Menu Categories */}
          {filteredData.length > 0 ? (
            <div className="space-y-20">
              {filteredData.map((category, index) => (
                <div
                  key={category.category}
                  ref={(el) => {
                    if (!isSearching) categoryRefs.current[index] = el;
                  }}
                >
                  <div className="flex items-center gap-4 mb-10">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className={`${category.icon} text-2xl text-[#C0392B]`}></i>
                    </div>
                    <h3 className="text-3xl font-light text-[#2D2D2D] tracking-wide">{category.category}</h3>
                    <div className="flex-grow h-px bg-[#F0E8E8]"></div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex gap-6 group">
                        <div className="w-28 h-28 flex-shrink-0 overflow-hidden rounded-sm">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex-grow">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-lg font-medium text-[#2D2D2D] tracking-wide">
                              {isSearching
                                ? highlightMatch(item.name, searchQuery.trim())
                                : item.name}
                            </h4>
                            <span className="text-lg font-medium text-[#C0392B] ml-4">${item.price}</span>
                          </div>
                          <p className="text-sm text-[#666666] leading-relaxed">{item.description}</p>
                          {item.spicy && (
                            <div className="flex items-center gap-1 mt-2">
                              <i className="ri-fire-fill text-[#C0392B] text-sm"></i>
                              <span className="text-xs text-[#C0392B]">Spicy</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* No results state */
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 flex items-center justify-center mb-6 text-[#E8D5D5]">
                <i className="ri-search-line text-6xl"></i>
              </div>
              <p className="text-xl font-light text-[#2D2D2D] mb-2">No dishes found</p>
              <p className="text-sm text-[#999]">Try a different keyword or browse by category above</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-6 px-6 py-2 rounded-full border border-[#C0392B] text-[#C0392B] text-sm hover:bg-[#C0392B] hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Clear Search
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function highlightMatch(text: string, query: string) {
  if (!query) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-[#FDECEA] text-[#C0392B] rounded px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}
